import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Search, ExternalLink, Building2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAccounts, useAdminStats, useCreateAccount } from "@/hooks/use-admin";
import { formatNumber } from "@/lib/utils";

export const Route = createFileRoute("/_auth/admin/")({
  component: AdminIndex,
});

const accountSchema = z.object({
  name: z.string().min(2, "Mínimo 2 caracteres"),
  type: z.enum(["administrada", "autoservicio", "agencia"]),
  tz: z.string().min(2),
  owner_name: z.string().min(2),
  owner_email: z.string().email("Email inválido"),
  owner_password: z.string().min(8, "Mínimo 8 caracteres"),
  seed_media_outlets: z.boolean().optional(),
  seed_peru_geo_full: z.boolean().optional(),
});
type AccountInput = z.infer<typeof accountSchema>;

function AdminIndex() {
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const stats = useAdminStats();
  const accounts = useAdminAccounts({ q });

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Administración</h1>
          <p className="text-sm text-ink-500 mt-1">Gestiona cuentas, usuarios y API keys del sistema.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/billing"><Button variant="outline">Facturación</Button></Link>
          <Link to="/admin/audit"><Button variant="outline">Audit log</Button></Link>
          <Button onClick={() => setShowModal(true)}>
            <Plus size={16} /> Nueva cuenta
          </Button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          ["Cuentas activas", stats.data?.accounts_active],
          ["Cuentas totales", stats.data?.accounts_total],
          ["Usuarios totales", stats.data?.users_count],
          ["Menciones DB", stats.data?.total_mentions],
        ].map(([label, val]) => (
          <Card key={String(label)}>
            <p className="text-xs text-ink-500">{label}</p>
            <p className="text-2xl font-bold text-ink-900 dark:text-white mt-1">
              {val == null ? "—" : formatNumber(val as number)}
            </p>
          </Card>
        ))}
      </div>

      {/* Filtro */}
      <Card className="mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input
            className="pl-10"
            placeholder="Buscar por nombre de cuenta…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      {/* Lista de cuentas */}
      <Card>
        <CardHeader>
          <CardTitle>Cuentas</CardTitle>
          <CardDescription>
            {accounts.data ? `${accounts.data.length} resultados` : "Cargando…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
          {accounts.isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 mb-2" />)
          ) : !accounts.data || accounts.data.length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">Sin cuentas.</p>
          ) : (
            accounts.data.map((a) => (
              <Link
                key={a.id}
                to="/admin/accounts/$accountId"
                params={{ accountId: String(a.id) }}
                className="py-3 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 px-2 rounded-md"
              >
                <Building2 size={18} className="text-ink-400" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink-900 dark:text-white">{a.name}</span>
                    <Badge variant={a.status === "active" ? "good" : a.status === "suspended" ? "bad" : "muted"}>
                      {a.status}
                    </Badge>
                    <Badge variant="muted">{a.type}</Badge>
                    {a.plan_code && <Badge>{a.plan_code}</Badge>}
                  </div>
                  <p className="text-xs text-ink-500 mt-1">
                    {a.users_count} usuarios · {a.projects_count} proyectos · creada{" "}
                    {new Date(a.created_at).toLocaleDateString("es-PE")}
                  </p>
                </div>
                <ExternalLink size={14} className="text-ink-400" />
              </Link>
            ))
          )}
        </CardContent>
      </Card>

      {showModal && <NewAccountModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

function NewAccountModal({ onClose }: { onClose: () => void }) {
  const create = useCreateAccount();
  const form = useForm<AccountInput>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      type: "autoservicio", tz: "America/Lima",
      seed_media_outlets: true, seed_peru_geo_full: false,
    },
  });
  const watchType = form.watch("type");
  const isAgency = watchType === "agencia";

  async function onSubmit(data: AccountInput) {
    try {
      // Solo enviar flags media si type=agencia
      const payload = isAgency ? data : {
        ...data,
        seed_media_outlets: false,
        seed_peru_geo_full: false,
      };
      await create.mutateAsync(payload);
      onClose();
    } catch {
      // toast lo muestra http.ts
    }
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Nueva cuenta</CardTitle>
          <CardDescription>Crea un tenant nuevo con su usuario owner inicial.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre de la cuenta</Label>
              <Input className="mt-1" {...form.register("name")} placeholder="Acme Inc." />
              {form.formState.errors.name && <p className="text-xs text-bad mt-1">{form.formState.errors.name.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <select
                  className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm"
                  {...form.register("type")}
                >
                  <option value="autoservicio">autoservicio</option>
                  <option value="administrada">administrada</option>
                  <option value="agencia">agencia</option>
                </select>
              </div>
              <div>
                <Label>Zona horaria</Label>
                <Input className="mt-1" {...form.register("tz")} />
              </div>
            </div>

            <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
              <p className="text-xs text-ink-500 mb-2">Owner inicial</p>
              <div className="space-y-2">
                <div>
                  <Label>Nombre</Label>
                  <Input className="mt-1" {...form.register("owner_name")} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" className="mt-1" {...form.register("owner_email")} />
                  {form.formState.errors.owner_email && (
                    <p className="text-xs text-bad mt-1">{form.formState.errors.owner_email.message}</p>
                  )}
                </div>
                <div>
                  <Label>Contraseña</Label>
                  <Input type="password" className="mt-1" {...form.register("owner_password")} />
                  {form.formState.errors.owner_password && (
                    <p className="text-xs text-bad mt-1">{form.formState.errors.owner_password.message}</p>
                  )}
                </div>
              </div>
            </div>

            {isAgency && (
              <div className="border-t border-ink-200 dark:border-ink-800 pt-3 space-y-2">
                <p className="text-xs font-semibold text-warn flex items-center gap-2">
                  📻 Bootstrap módulo Medios Tradicionales
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    {...form.register("seed_media_outlets")}
                  />
                  <div>
                    <div className="text-sm">Cargar catálogo de medios referenciales</div>
                    <div className="text-xs text-ink-500">
                      ~27 outlets del PDF SEAL: El Peruano, RPP, Exitosa, HBA Noticias, etc. Idempotente.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    {...form.register("seed_peru_geo_full")}
                  />
                  <div>
                    <div className="text-sm">Cargar catálogo geo INEI completo</div>
                    <div className="text-xs text-ink-500">
                      25 regiones + 196 provincias + 1872 distritos. Global (compartido entre cuentas). Tarda ~5s. Skip si ya está cargado.
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creando…" : "Crear cuenta"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
